import { Flex, Button } from '@radix-ui/themes';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../../../shared/Notification/useNotification';
import { UploadCategoryModal } from './UploadCategoryModal.jsx';

const MB_SIZE = 1024 * 1024;
const MAX_FILE_SIZE = 50 * MB_SIZE;
const fileTypes = import.meta.env.VITE_VAULT_FILE_FORMATS;

export const UploadFile = ({
  organizationId,
  uploadFiles,
  categories,
  vendors,
  defaultCategory,
}) => {
  const { t } = useTranslation('vault');
  const fileInputRef = useRef(null);
  const { showSuccess, showError } = useNotification();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);

  const validateFiles = files => {
    const allowedTypes = fileTypes
      .split(',')
      .map(type => type.trim().toLowerCase());
    const validFiles = [];
    const errors = [];

    files.forEach(file => {
      const fileExtension = file.name
        .split('.')
        .pop()
        ?.toLowerCase();
      const fileSizeMB = (file.size / MB_SIZE).toFixed(1);

      if (!allowedTypes.includes(fileExtension)) {
        errors.push(
          t('invalidFileType', {
            fileName: file.name,
            allowedTypes: fileTypes,
          }),
        );
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        errors.push(
          t('fileTooLarge', {
            fileName: file.name,
            fileSize: fileSizeMB,
            maxSize: '50.0',
          }),
        );
        return;
      }

      validFiles.push(file);
    });

    return { validFiles, errors };
  };

  const handleFileInputChange = e => {
    const files = Array.from(e.target.files);
    const input = e.target;

    if (files.length > 0) {
      const { validFiles, errors } = validateFiles(files);

      if (errors.length > 0) {
        errors.forEach(error => showError({ content: error }));
      }

      if (validFiles.length > 0) {
        setSelectedFiles(prev => [...prev, ...validFiles]);
      }
    }
    input.value = '';
  };

  const handleRemoveFile = index => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddMoreFiles = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async (category, vendorNames) => {
    if (selectedFiles.length === 0) return;

    setModalOpen(false);

    try {
      await uploadFiles(
        selectedFiles,
        organizationId,
        category?.value || null,
        category?.label || null,
        vendorNames,
      );
      showSuccess({
        content: t('filesUploadStarted', { count: selectedFiles.length }),
      });
    } catch (error) {
      showError({ content: error.message });
    }

    setSelectedFiles([]);
  };

  const handleCancel = () => {
    setModalOpen(false);
    setSelectedFiles([]);
  };

  return (
    <Flex justify="end">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept={fileTypes
          .split(',')
          .map(type => `.${type}`)
          .join(',')}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      <Button size="3" variant="solid" onClick={() => setModalOpen(true)}>
        <i className="fa-solid fa-plus" />
        {t('upload')}
      </Button>
      <UploadCategoryModal
        open={modalOpen}
        onOpenChange={isOpen => {
          if (!isOpen) handleCancel();
        }}
        selectedFiles={selectedFiles}
        onRemoveFile={handleRemoveFile}
        onAddMoreFiles={handleAddMoreFiles}
        onUpload={handleUpload}
        categories={categories}
        vendors={vendors}
        defaultCategory={defaultCategory}
      />
    </Flex>
  );
};
